<script lang="ts" setup>
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import MLwChart from '@/components/charts/MLwChart.vue';
import LwChart from '@/components/charts/LwChart.vue';
import { TickUtils, type Tick } from '@/utils/ticks';
import type { OhlcvTuple } from '@/utils/types';
import { useAutoRefresh } from '@/composable/useAutorefresh';
import { Divider } from 'primevue';

const mlw = useTemplateRef("mlw")
const lw = useTemplateRef("lw")
const loading = ref(true)
const mode = ref(1)
const ohlcv = ref<OhlcvTuple[]>([])
const spreads = ref<Tick[]>([])
const askBid = ref(false)
const linesMode = ref(1)  // 1 = market ticks, 2 = cex ask/ dexbid, 3 dex ask/cex bid

interface LegendItem {
  name: string
  label: string
}

const props = defineProps<{
  ticksLeft?: Tick[],
  ticksRight?: Tick[],
  asksLeft?: Tick[],
  bidsLeft?: Tick[],
  asksRight?: Tick[],
  bidsRight?: Tick[],
  minMove: number | null,
  sl: number | null
  tp: number | null
  height?: string,
  showAbBa?: boolean,
  legend?: LegendItem[],
  exBuy: string,
  exSell: string
}>()

const showAbBa = computed(() => props.showAbBa ?? false)
const height = computed(() => props.height ?? "400px")

const MarketBuyColor = "#26a69a"
const MarketSellColor = "#ef5350"

const AskLeftColor = "#c800ff66"
const BidLeftColor = "#00a6ff66"

const AskRightColor = "#c800ff66"
const BidRightColor = "#00a6ff66"

onMounted(async () => {
  await nextTick()

  mlw.value?.addSeries("ticks-left", MarketBuyColor)
  mlw.value?.addSeries("ticks-right", MarketSellColor)

  mlw.value?.addSeries("ask-left", AskLeftColor)
  mlw.value?.addSeries("bid-left", BidLeftColor)

  mlw.value?.setSeriesVisibility("ask-left", false)
  mlw.value?.setSeriesVisibility("bid-left", false)

  mlw.value?.addSeries("ask-right", AskRightColor)
  mlw.value?.addSeries("bid-right", BidRightColor)

  mlw.value?.setSeriesVisibility("ask-right", false)
  mlw.value?.setSeriesVisibility("bid-right", false)

  askBid.value = showAbBa.value
  await nextTick()
  drawStopLoss(props.sl)
  drawTakeProfit(props.tp)
})

const toggleAskBid = () => {
  askBid.value = !askBid.value
  mlw.value?.setSeriesVisibility("ask-left", askBid.value)
  mlw.value?.setSeriesVisibility("bid-left", askBid.value)
  mlw.value?.setSeriesVisibility("ask-right", askBid.value)
  mlw.value?.setSeriesVisibility("bid-right", askBid.value)
}

const buildSpreads = () => {
  if (!props.ticksLeft || !props.ticksRight) {
    ohlcv.value = []
    return
  }

  const tillTs = +new Date() / 1000
  const fromTs = tillTs - 3600 * 10
  const leftTicksFilled = TickUtils.fillTicksTo(props.ticksLeft, tillTs, fromTs)
  const rightTicksFilled = TickUtils.fillTicksTo(props.ticksRight, tillTs, fromTs)
  spreads.value = TickUtils.calcSpreads(leftTicksFilled, rightTicksFilled).slice(-3600 * 10)

  ohlcv.value = TickUtils.ticksToOhlcv(spreads.value)
  loading.value = ohlcv.value.length == 0
}

const spreadsRefresher = useAutoRefresh(() => { buildSpreads() }, 1000)

watch(() => props.ticksLeft, (v) => {
  pushTick("ticks-left", v)
})

watch(() => props.ticksRight, (v) => {
  pushTick("ticks-right", v)
})

watch(() => props.asksLeft, (v) => {
  pushTick("ask-left", v)
})

watch(() => props.bidsLeft, (v) => {
  pushTick("bid-left", v)
})

watch(() => props.asksRight, (v) => {
  pushTick("ask-right", v)
})

watch(() => props.bidsRight, (v) => {
  pushTick("bid-right", v)
})

watch(linesMode, v => {
  if (v === 1) {
    askBid.value = false
    mlw.value?.setSeriesVisibility("ticks-left", true)
    mlw.value?.setSeriesVisibility("ticks-right", true)
    mlw.value?.setSeriesVisibility("ask-left", false)
    mlw.value?.setSeriesVisibility("bid-left", false)
    mlw.value?.setSeriesVisibility("ask-right", false)
    mlw.value?.setSeriesVisibility("bid-right", false)

    mlw.value?.setSeriesColor("ticks-left", MarketBuyColor)
    mlw.value?.setSeriesColor("ticks-right", MarketSellColor)
    mlw.value?.setSeriesColor("ask-left", AskLeftColor)
    mlw.value?.setSeriesColor("bid-left", BidLeftColor)
    mlw.value?.setSeriesColor("ask-right", AskRightColor)
    mlw.value?.setSeriesColor("bid-right", BidRightColor)


  } else if (v === 2) {
    askBid.value = true
    mlw.value?.setSeriesVisibility("ticks-left", false)
    mlw.value?.setSeriesVisibility("ticks-right", false)
    mlw.value?.setSeriesVisibility("ask-left", true)
    mlw.value?.setSeriesVisibility("bid-left", false)
    mlw.value?.setSeriesVisibility("ask-right", false)
    mlw.value?.setSeriesVisibility("bid-right", true)

    mlw.value?.setSeriesColor("ask-left", MarketBuyColor)
    mlw.value?.setSeriesColor("bid-right", MarketSellColor)

  } else if (v === 3) {
    askBid.value = true
    mlw.value?.setSeriesVisibility("ticks-left", false)
    mlw.value?.setSeriesVisibility("ticks-right", false)
    mlw.value?.setSeriesVisibility("ask-left", false)
    mlw.value?.setSeriesVisibility("bid-left", true)
    mlw.value?.setSeriesVisibility("ask-right", true)
    mlw.value?.setSeriesVisibility("bid-right", false)

    mlw.value?.setSeriesColor("bid-left", MarketBuyColor)
    mlw.value?.setSeriesColor("ask-right", MarketSellColor)
  }
})

const drawStopLoss = (v: number | null) => {
  if (v) {
    mlw.value?.setPriceLine("ticks-right", "dexStopLossPrice", "Stop-Loss", v)
  } else {
    mlw.value?.removePriceLine("ticks-right", "dexStopLossPrice")
  }
}

const drawTakeProfit = (v: number | null) => {
  if (v) {
    mlw.value?.setPriceLine("ticks-right", "dexTakeProfitPrice", "Take-Profit", v)
  } else {
    mlw.value?.removePriceLine("ticks-right", "dexTakeProfitPrice")
  }
}

watch(() => props.sl, v => {
  drawStopLoss(v)
})

watch(() => props.tp, v => {
  drawTakeProfit(v)
})

onMounted(() => {
  spreadsRefresher.startRefresh()
})
onBeforeUnmount(() => {
  spreadsRefresher.stopRefresh()
})

const pushTick = (series: string, newTicks: Tick[] | undefined, forceReplace = false) => {
  if (!newTicks || newTicks.length === 0) return

  const data = mlw.value?.getSeriesData(series)

  // Если данных нет или forceReplace – полная замена
  if (forceReplace || !data || data.length === 0) {
    mlw.value?.updateSeries(series, newTicks.slice(-3600 * 3))
    return
  }

  // Инкрементальное добавление (всех новых тиков, которые позже последнего)
  const lastPointTime = data[data.length - 1].time
  const ticksToAdd = newTicks.filter(tick => tick[0] > lastPointTime)

  for (const tick of ticksToAdd) {
    mlw.value?.pushIntoSeries(series, tick)
  }
}

const resetView = () => {
  mlw.value?.reset()
  lw.value?.reset()
}
defineExpose({ toggleAskBid, askBid, resetView, spreads })

</script>

<template>
  <div>
    <div class="flex items-center justify-between py-2 px-4 border-b-1 border-color">
      <span class="pit">{{ $t('spreadsChart.title') }}</span>
      <div class="gap-2 flex">
        <Button :variant="mode == 1 ? undefined : 'outlined'" v-tooltip.bottom="$t('tooltip.linePrice')" size="small"
          severity="primary" icon="pi pi-chart-line" @click="mode = 1">
        </Button>
        <Button :variant="mode == 2 ? undefined : 'outlined'" size="small"
          v-tooltip.bottom="$t('tooltip.realSpreadChart')" severity="primary" icon="pi pi-chart-bar" @click="mode = 2">
        </Button>
        <Button :variant="mode == 3 ? undefined : 'outlined'" size="small" severity="primary" icon="pi pi-expand"
          v-tooltip.bottom="$t('tooltip.bothCharts')" @click="mode = 3">
        </Button>
        <Button v-if="!showAbBa" :variant="askBid ? undefined : 'outlined'" size="small" severity="primary"
          icon="pi pi-book" v-tooltip.bottom="$t('tooltip.drawOb')" @click="toggleAskBid()">
        </Button>
        <Button variant="outlined" size="small" v-tooltip.bottom="$t('tooltip.goHome')" severity="primary"
          icon="pi pi-home" @click="resetView()">
        </Button>

        <template v-if="showAbBa">
          <Divider layout="vertical" class="mx-0" />
          <Button :variant="linesMode == 1 ? undefined : 'outlined'" size="small" severity="primary"
            icon="pi pi-shopping-cart" v-tooltip.bottom="$t('tooltip.marketPrice')" @click="linesMode = 1">
          </Button>
          <Button :variant="linesMode == 2 ? undefined : 'outlined'"
            v-tooltip.bottom="$t('tooltip.realCexDexPrice', { buy: props.exBuy, sell: props.exSell })" size="small"
            severity="primary" icon="pi pi-arrow-up" @click="linesMode = 2">
          </Button>
          <Button :variant="linesMode == 3 ? undefined : 'outlined'"
            v-tooltip.bottom="$t('tooltip.realCexDexPrice', { buy: props.exSell, sell: props.exBuy })" size="small"
            severity="primary" icon="pi pi-arrow-down" @click="linesMode = 3">
          </Button>
        </template>
      </div>
    </div>

    <div v-if="loading" :style="`height: ${height}`" class="flex items-center justify-center">
      <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="8" animationDuration=".5s" />
    </div>
    <template v-else>
      <div v-if="ohlcv && ohlcv?.length == 0" :style="`height: ${height}`"
        class="flex items-center justify-center sr-text">
        {{ $t('loadingError') }}
      </div>
      <!-- Нет данных -->
      <div v-else-if="!ohlcv || ohlcv.length == 0" :style="`height: ${height}`"
        class="flex items-center justify-center sr-text">
        {{ $t('noDataError') }}
      </div>
    </template>
    <div class="grid" :class="{ 'grid-cols-2': mode == 3, 'grid-cols-1': mode != 3 }">
      <!-- График линейный -->
      <div :class="{ 'border-r-1 border-color': mode == 3 }" v-show="(mode == 1 || mode == 3) && !loading">
        <MLwChart ref="mlw" :height="height" y-format="1s" :min-move="props.minMove" :legend="props.legend">
        </MLwChart>
      </div>
      <!-- График свечной -->
      <div v-show="(mode == 2 || mode == 3) && !loading && ohlcv && ohlcv.length > 0">
        <LwChart y-format="1s" type="ohlcv" :data="ohlcv" :minMove="0.01" ref="lw" :height="height">
        </LwChart>
      </div>
    </div>
  </div>
</template>
